import { useState, useRef } from 'react';
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';

import './styles.css';

const getInitials = (name = '') => {
	const words = name.trim().split(/\s+/).filter(Boolean);
	return words
		.slice(0, 2)
		.map((w) => w[0].toUpperCase())
		.join('');
};

const OrganizationCard = ({ organization, inAddMode = false, onNameChange, onSubmit, onCancel, onDelete, onRename }) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const [isRenaming, setIsRenaming] = useState(false);
	const [newName, setNewName] = useState(organization?.name || '');
	const open = Boolean(anchorEl);
	const navigate = useNavigate();
	const menuJustClosed = useRef(false);

	const handleCardClick = () => {
		if (inAddMode || isRenaming || menuJustClosed.current) return;
		navigate(`/admin/organizations/${organization.id}/directories`);
	};

	const handleMenuOpen = (e) => {
		e.stopPropagation();
		setAnchorEl(e.currentTarget);
	};

	const handleMenuClose = (e) => {
		if (e) e.stopPropagation();
		menuJustClosed.current = true;
		setAnchorEl(null);
		setTimeout(() => { menuJustClosed.current = false; }, 0);
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter') onSubmit?.();
		if (e.key === 'Escape') onCancel?.();
	};

	const handleRenameSubmit = () => {
		if (newName.trim()) {
			onRename?.(organization, newName.trim());
		}
		setIsRenaming(false);
	};

	const handleRenameKeyDown = (e) => {
		if (e.key === 'Enter') handleRenameSubmit();
		if (e.key === 'Escape') setIsRenaming(false);
	};

	return (
		<div className={`org-card${inAddMode ? ' org-card--add-mode' : ''}`} onClick={handleCardClick}>
			<div className="org-card__strip" />

			<div className="org-card__body">
				<div className="org-card__avatar">{getInitials(organization.name)}</div>
				<div className="org-card__text">
					{inAddMode ? (
					<>
						<input
							className="org-card__name-input"
							autoFocus
							placeholder="Organization name"
							value={organization.name}
							onChange={(e) => onNameChange?.(e.target.value)}
							onKeyDown={handleKeyDown}
							onClick={(e) => e.stopPropagation()}
						/>
						<div className="org-card__add-actions" onClick={(e) => e.stopPropagation()}>
							<button className="org-card__add-confirm" onClick={onSubmit}>Add</button>
							<button className="org-card__add-cancel" onClick={onCancel}>Cancel</button>
						</div>
					</>
					) : isRenaming ? (
					<>
						<input
							className="org-card__name-input"
							autoFocus
							value={newName}
							onChange={(e) => setNewName(e.target.value)}
							onKeyDown={handleRenameKeyDown}
							onClick={(e) => e.stopPropagation()}
						/>
						<div className="org-card__add-actions" onClick={(e) => e.stopPropagation()}>
							<button className="org-card__add-confirm" onClick={handleRenameSubmit}>Save</button>
							<button className="org-card__add-cancel" onClick={() => setIsRenaming(false)}>Cancel</button>
						</div>
					</>
					) : (
						<p className="org-card__name">{organization.name}</p>
					)}
					<p className="org-card__meta">Organization</p>
				</div>
				{!inAddMode && !isRenaming && (
					<>
						<IconButton
							className="org-card__menu-btn"
							size="small"
							onClick={handleMenuOpen}
							aria-label="Organization options"
						>
							<MoreVertIcon fontSize="small" />
						</IconButton>
						<Menu
							anchorEl={anchorEl}
							open={open}
							onClose={handleMenuClose}
							onClick={(e) => e.stopPropagation()}
							PaperProps={{ sx: { minWidth: 140, borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' } }}
						>
							<MenuItem
								onClick={(e) => {
									e.stopPropagation();
									setIsRenaming(true);
									setNewName(organization.name);
									handleMenuClose(e);
								}}
								sx={{ fontSize: '13.5px', fontFamily: 'Open Sans, sans-serif' }}
							>
								Rename
							</MenuItem>
							<MenuItem
								onClick={(e) => {
									e.stopPropagation();
									onDelete(organization);
									handleMenuClose(e);
								}}
								sx={{ fontSize: '13.5px', color: '#c0392b', fontFamily: 'Open Sans, sans-serif' }}
							>
								Delete
							</MenuItem>
						</Menu>
					</>
				)}
			</div>

			{!inAddMode && !isRenaming && (
				<div className="org-card__footer">
					<span className="org-card__open-label">Open organization</span>
					<ArrowForwardIcon className="org-card__arrow" />
				</div>
			)}
		</div>
	);
};

export default OrganizationCard;
